import type { StoryIssue } from 'common/building-blocks/link-capability';
import { COMPONENT_DISPLAY_NAME } from 'common/constants';
import type { ComponentTypes } from 'types/story';

function getTypeName(type: ComponentTypes): string {
	return COMPONENT_DISPLAY_NAME[type as keyof typeof COMPONENT_DISPLAY_NAME] ?? type;
}

/**
 * Maps a StoryIssue to editor-facing title, description, and severityLabel.
 *
 * Story Issues groups validation problems by card in story order. In the card
 * editor, the current card appears first and is visually marked as current.
 *
 * Issue copy must help an editor identify the affected element, understand the
 * problem, and correct it using the current Inspector or Layers UI.
 *
 * - Name the affected element by its editor-visible name. Name an existing
 *   destination by its card or element name.
 * - Use the same field labels and nouns as the editor, such as “Link to Card”,
 *   “Scroll to”, card, and element.
 * - Explain the user-visible conflict or missing configuration, then give a
 *   concrete corrective action.
 * - Never expose model IDs, node IDs, component constants, property keys, or
 *   other implementation details.
 * - Do not rely on technical identifiers to distinguish similar issues; provide
 *   useful editor context instead.
 */
export function getStoryIssuePresentation(issue: StoryIssue): {
	title: string;
	description: string;
	severityLabel: string;
} {
	const outerType = getTypeName(issue.primaryElementType);
	const related = issue.relatedElements?.[0];
	const relatedType = related ? getTypeName(related.type) : 'Link';
	const severityLabel = issue.severity === 'error' ? 'Needs fixing' : 'Check this';

	switch (issue.code) {
		case 'interactive-descendant-conflict':
			return {
				title: `${relatedType} inside linked ${outerType}`,
				description: `“${related?.name ?? relatedType}” and “${issue.primaryElementName}” both respond to clicks. Move one outside the other, or remove the outer link.`,
				severityLabel,
			};
		case 'nested-inline-anchor':
			return {
				title: `Text link inside linked ${outerType}`,
				description: `The link inside “${related?.name ?? relatedType}” conflicts with “${issue.primaryElementName}”. Remove one of the links.`,
				severityLabel,
			};
		case 'opaque-descendant':
			return {
				title: `${relatedType} inside linked ${outerType}`,
				description: `“${related?.name ?? relatedType}” may contain its own controls. Move it outside “${issue.primaryElementName}”, or remove the outer link.`,
				severityLabel,
			};
		case 'missing-internal-card':
			return {
				title: `“${issue.primaryElementName}” links to a missing card`,
				description: `“Link to Card” on “${issue.primaryElementName}” points to a card that no longer exists. Choose another card or clear “Link to Card”.`,
				severityLabel,
			};
		case 'missing-internal-block':
			return {
				title: `“${issue.primaryElementName}” scrolls to a missing element`,
				description: `“Scroll to” on “${issue.primaryElementName}” points to an element that no longer exists on “${issue.context.targetCardName}”. Choose another element or clear “Scroll to”.`,
				severityLabel,
			};
		default:
			return {
				title: 'Story issue',
				description: `Review “${issue.primaryElementName}” before publishing.`,
				severityLabel,
			};
	}
}
